import { concepts, graphemes, lessonById, mappings, phonemes, words, wordById } from '../data/foundations'
import type { ConceptLearningRecord, EvidenceCounters, LearningAttempt, LearningRecords, LearningSkill, MasteryStats, Progress } from '../types/progress'
import { getNativeProgressPersistence, progressStorageKey } from './nativeProgressPersistence'

export interface ProgressRepository {
  load(): Progress
  save(progress: Progress): void
}

export const emptyMastery = (): MasteryStats => ({ correct: 0, incorrect: 0, consecutiveCorrect: 0, lastPracticed: null, masteryScore: 0 })
const masteryRecord = (ids: string[]) => Object.fromEntries(ids.map((id) => [id, emptyMastery()]))

export const createInitialProgress = (): Progress => ({
  version: 2,
  inventoryVersion: 'GA_FOUNDATIONS_V1',
  currentConceptId: concepts[0].id,
  completedLessons: [],
  stars: 0,
  plantLevel: 0,
  unlockedItems: [],
  phonemeMastery: masteryRecord(phonemes.map((item) => item.id)),
  graphemeMastery: masteryRecord(graphemes.map((item) => item.id)),
  mappingMastery: masteryRecord(mappings.map((item) => item.id)),
  blendingMastery: emptyMastery(),
  segmentingMastery: emptyMastery(),
  wordDecodingMastery: masteryRecord(words.map((item) => item.id)),
  learningRecords: { concepts: {}, skills: {}, words: {} },
  attemptHistory: [],
  dailyRecords: [],
  migration: { fromVersion: null, migratedAt: null },
})

const normalizeRecord = (fallback: Record<string, MasteryStats>, value: unknown) => {
  const parsed = value && typeof value === 'object' ? value as Record<string, Partial<MasteryStats>> : {}
  return Object.fromEntries(Object.keys(fallback).map((id) => [id, { ...emptyMastery(), ...(parsed[id] ?? {}) }]))
}

const skillIds = new Set<LearningSkill>(['sound_discrimination', 'mapping', 'blending', 'segment_count', 'segment_sequence', 'encoding', 'word_recognition', 'decoding'])
const asObject = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
const count = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
const isoDate = (value: unknown) => typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null
const dayDate = (value: unknown) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(`${value}T12:00:00`)) ? value : null
const counters = (value: unknown): EvidenceCounters => {
  const item = asObject(value)
  return { independentCorrect: count(item.independentCorrect), independentIncorrect: count(item.independentIncorrect), supportedCorrect: count(item.supportedCorrect), supportedAttempts: count(item.supportedAttempts), participation: count(item.participation) }
}

/** New observations default to empty; historical practice scores are never upgraded. */
const normalizeLearningRecords = (value: unknown): LearningRecords => {
  const parsed = asObject(value)
  const next: LearningRecords = { concepts: {}, skills: {}, words: {} }
  for (const [id, value] of Object.entries(asObject(parsed.skills))) {
    if (skillIds.has(id as LearningSkill)) next.skills[id as LearningSkill] = counters(value)
  }
  for (const [id, value] of Object.entries(asObject(parsed.concepts))) {
    const item = asObject(value)
    const lesson = typeof item.lastLessonId === 'string' ? lessonById.get(item.lastLessonId) : undefined
    const lastPracticed = isoDate(item.lastPracticed)
    const nextReviewDate = dayDate(item.nextReviewDate)
    if (!lesson || lesson.conceptId !== id || !lastPracticed || !nextReviewDate || !skillIds.has(item.lastSkill as LearningSkill)) continue
    const record: ConceptLearningRecord = {
      ...counters(item), lastLessonId: lesson.id, lastSkill: item.lastSkill as LearningSkill, lastPracticed,
      nextReviewDate, reviewIntervalDays: item.reviewIntervalDays === 3 || item.reviewIntervalDays === 7 ? item.reviewIntervalDays : 1,
      lastIndependentDate: dayDate(item.lastIndependentDate), lastIntervalAdvancedDate: dayDate(item.lastIntervalAdvancedDate), needsSupport: item.needsSupport === true,
    }
    if (skillIds.has(item.lastIndependentSkill as LearningSkill)) record.lastIndependentSkill = item.lastIndependentSkill as LearningSkill
    record.supportSkills = Array.isArray(item.supportSkills) ? [...new Set(item.supportSkills.filter((skill): skill is LearningSkill => skillIds.has(skill as LearningSkill)))] : record.needsSupport ? [record.lastSkill] : []
    record.needsSupport = record.supportSkills.length > 0
    if (typeof item.lastWordId === 'string' && wordById.has(item.lastWordId)) record.lastWordId = item.lastWordId
    next.concepts[id] = record
  }
  for (const [id, value] of Object.entries(asObject(parsed.words))) {
    const item = asObject(value)
    const lastPracticed = isoDate(item.lastPracticed)
    if (!wordById.has(id) || !lastPracticed) continue
    next.words[id] = {
      exposures: count(item.exposures), independentDecodingCorrect: count(item.independentDecodingCorrect), independentDecodingIncorrect: count(item.independentDecodingIncorrect),
      transferCorrect: Math.min(1, count(item.transferCorrect)), encodingTransferCorrect: Math.min(1, count(item.encodingTransferCorrect)), lastPracticed,
    }
  }
  return next
}

const normalizeAttemptHistory = (value: unknown): LearningAttempt[] => {
  if (!Array.isArray(value)) return []
  return value.slice(-500).flatMap((entry) => {
    const item = asObject(entry)
    const lesson = typeof item.lessonId === 'string' ? lessonById.get(item.lessonId) : undefined
    const at = isoDate(item.at)
    if (!lesson || item.conceptId !== lesson.conceptId || !at || !skillIds.has(item.skill as LearningSkill) || typeof item.correct !== 'boolean') return []
    const assistanceLevel = item.assistanceLevel === 0 || item.assistanceLevel === 1 || item.assistanceLevel === 2 ? item.assistanceLevel : 3
    const attemptNumber = Math.max(1, count(item.attemptNumber))
    const adultVerified = item.adultVerified === true
    const evidence = item.evidence === 'checked' && (item.skill !== 'decoding' || adultVerified) ? 'checked' : 'practice'
    const optionCount = typeof item.optionCount === 'number' ? count(item.optionCount) : undefined
    const independent = item.independent === true && evidence === 'checked' && assistanceLevel === 0 && attemptNumber === 1 && (optionCount === undefined || optionCount >= 2)
    const attempt: LearningAttempt = {
      lessonId: lesson.id, conceptId: lesson.conceptId, at, skill: item.skill as LearningSkill, correct: item.correct,
      assistanceLevel, attemptNumber, adultVerified, evidence, independent,
      mode: item.mode === 'independent' || item.mode === 'transfer' || item.mode === 'review' ? item.mode : 'lesson',
      assessmentPhase: item.assessmentPhase === 'observation' && item.skill === 'segment_count' && item.correct ? 'observation' : 'final',
      transfer: item.transfer === true && independent && item.correct && item.skill === 'decoding' && item.mode === 'transfer',
      encodingTransfer: item.encodingTransfer === true && independent && item.correct && item.skill === 'encoding' && item.mode === 'transfer',
    }
    if (typeof item.wordId === 'string' && wordById.has(item.wordId)) attempt.wordId = item.wordId
    if (optionCount !== undefined) attempt.optionCount = optionCount
    if (typeof item.responseMs === 'number' && Number.isFinite(item.responseMs)) attempt.responseMs = Math.max(0, item.responseMs)
    return [attempt]
  })
}

const legacyConcept: Record<string, string> = { s: 'gpc_s_s', a: 'gpc_a_short', t: 'gpc_t_t', p: 'gpc_p_p', i: 'gpc_i_short', n: 'gpc_n_n' }
const legacyPhoneme: Record<string, string> = { s: 'phoneme_s', a: 'phoneme_short_a_ae', t: 'phoneme_t', p: 'phoneme_p', i: 'phoneme_short_i_ih', n: 'phoneme_n' }

interface LegacyProgress {
  version?: 1
  currentUnit?: number
  completedLessons?: string[]
  stars?: number
  plantLevel?: number
  unlockedItems?: string[]
  wordProgress?: Record<string, number>
  phonemeStats?: Record<string, Partial<MasteryStats>>
  dailyRecords?: Progress['dailyRecords']
}

export const migrateLegacyProgress = (value: LegacyProgress): Progress => {
  const next = createInitialProgress()
  const oldOrder = ['s', 'a', 't', 'p', 'i', 'n']
  const completedLessons = (value.completedLessons ?? []).flatMap((legacyId) => {
    const match = legacyId.match(/^([satpin])-(hear|recognition|sound-hunt|blend|word-challenge)-01$/)
    return match ? [`lesson_${legacyConcept[match[1]]}_${match[2]}`] : []
  })
  oldOrder.forEach((letter) => {
    const old = value.phonemeStats?.[letter]
    if (!old) return
    const stats = { ...emptyMastery(), ...old }
    next.phonemeMastery[legacyPhoneme[letter]] = stats
    const concept = concepts.find((item) => item.id === legacyConcept[letter])
    concept?.graphemeIds.forEach((id) => { next.graphemeMastery[id] = stats })
    concept?.mappingIds.forEach((id) => { next.mappingMastery[id] = stats })
  })
  const currentIndex = Math.max(0, Math.min(oldOrder.length - 1, (value.currentUnit ?? 1) - 1))
  return {
    ...next,
    currentConceptId: legacyConcept[oldOrder[currentIndex]],
    completedLessons,
    stars: value.stars ?? 0,
    plantLevel: value.plantLevel ?? 0,
    unlockedItems: Array.isArray(value.unlockedItems) ? value.unlockedItems : [],
    wordDecodingMastery: Object.fromEntries(words.map((word) => {
      const attempts = value.wordProgress?.[word.spelling] ?? value.wordProgress?.[word.id] ?? 0
      return [word.id, { ...emptyMastery(), correct: attempts, consecutiveCorrect: attempts, masteryScore: Math.min(100, attempts * 10) }]
    })),
    dailyRecords: Array.isArray(value.dailyRecords) ? value.dailyRecords.slice(-14) : [],
    migration: { fromVersion: 1, migratedAt: new Date().toISOString() },
  }
}

export const normalizeProgress = (value: unknown): Progress => {
  const fallback = createInitialProgress()
  if (!value || typeof value !== 'object') return fallback
  const parsed = value as Partial<Progress>
  if (parsed.version !== 2) return migrateLegacyProgress(value as LegacyProgress)
  return {
    ...fallback,
    ...parsed,
    version: 2,
    inventoryVersion: 'GA_FOUNDATIONS_V1',
    currentConceptId: concepts.some((item) => item.id === parsed.currentConceptId) ? parsed.currentConceptId! : fallback.currentConceptId,
    completedLessons: Array.isArray(parsed.completedLessons) ? parsed.completedLessons : [],
    unlockedItems: Array.isArray(parsed.unlockedItems) ? parsed.unlockedItems : [],
    phonemeMastery: normalizeRecord(fallback.phonemeMastery, parsed.phonemeMastery),
    graphemeMastery: normalizeRecord(fallback.graphemeMastery, parsed.graphemeMastery),
    mappingMastery: normalizeRecord(fallback.mappingMastery, parsed.mappingMastery),
    wordDecodingMastery: normalizeRecord(fallback.wordDecodingMastery, parsed.wordDecodingMastery),
    blendingMastery: { ...emptyMastery(), ...parsed.blendingMastery },
    segmentingMastery: { ...emptyMastery(), ...parsed.segmentingMastery },
    learningRecords: normalizeLearningRecords(parsed.learningRecords),
    attemptHistory: normalizeAttemptHistory(parsed.attemptHistory),
    dailyRecords: Array.isArray(parsed.dailyRecords) ? parsed.dailyRecords.slice(-14) : [],
  }
}

export class LocalStorageProgressRepository implements ProgressRepository {
  private readonly key = progressStorageKey
  private readonly legacyKey = 'phonics-garden.progress.v1'

  load() {
    const native = getNativeProgressPersistence()?.getCurrent()
    if (native !== undefined) return normalizeProgress(JSON.parse(native))
    try {
      const current = localStorage.getItem(this.key)
      if (current) return normalizeProgress(JSON.parse(current))
      const legacy = localStorage.getItem(this.legacyKey)
      if (legacy) {
        const migrated = migrateLegacyProgress(JSON.parse(legacy) as LegacyProgress)
        return migrated
      }
      return createInitialProgress()
    } catch {
      return createInitialProgress()
    }
  }

  save(progress: Progress) {
    const serialized = JSON.stringify(progress)
    const native = getNativeProgressPersistence()
    if (native) native.save(serialized)
    else localStorage.setItem(this.key, serialized)
  }
}
